import { useQuery } from '@apollo/client';
import Link from 'next/link';
import { GET_SHORT_PROFILE } from '../../../helpers/graphql/profile';
import { GET_AUTHOR_RECENT_POSTS } from '../../../helpers/graphql/posts';
import { fraunces } from '../../Landing/fonts';
import AuthorName from '../../General/AuthorName';
import FollowButton from '../../Profile/FollowButton';
import ProfileAvatar from '../../Profile/ProfileAvatar';
import SocialBar from '../../Profile/SocialBar';
import FutureImage from '../../FutureImage';

// Author card after the article — avatar/name render from static props,
// bio, social links and recent posts are fetched once the card mounts.
// On very wide logged-out screens the ConversionRail shows a compact
// author box instead (`hideOnRail`).
export default function AuthorCard({
  author,
  currentPermlink,
  hideOnRail,
  label = 'Written by',
}) {
  const { username, avatar } = author;

  const { data } = useQuery(GET_SHORT_PROFILE, {
    variables: { username },
    ssr: false,
  });
  const { data: postsData } = useQuery(GET_AUTHOR_RECENT_POSTS, {
    variables: { author: username, limit: 4 },
    ssr: false,
  });

  const about = data && data.profile ? data.profile.about : undefined;
  const recentPosts = (postsData && postsData.posts ? postsData.posts : [])
    .filter(post => post.permlink !== currentPermlink && post.thumb)
    .slice(0, 3);

  return (
    <section
      className={`${
        hideOnRail ? 'tf-hide-on-rail-author ' : ''
      }tw-mx-auto tw-my-10 tw-max-w-[46rem] tw-px-4`}
    >
      <div className="tw-rounded-2xl tw-border tw-border-solid tw-border-[#E7E0CF] dark:tw-border-[#26382F] tw-bg-[#FFFDF6] dark:tw-bg-[#15231E] tw-p-6 tw-shadow-[0_24px_50px_-30px_rgba(29,45,40,0.4)] sm:tw-p-7">
        <div className="tw-flex tw-items-start tw-gap-5">
          <Link href={`/@${username}`} prefetch={false} className="tw-shrink-0">
            <ProfileAvatar authorAvatar={avatar} author={username} size={64} />
          </Link>
          <div className="tw-min-w-0 tw-flex-1">
            <p className="tw-m-0 tw-font-mono tw-text-[10px] tw-font-semibold tw-uppercase tw-tracking-[0.25em] tw-text-gray-400">
              {label}
            </p>
            <Link
              href={`/@${username}`}
              prefetch={false}
              className="tw-no-underline"
            >
              <AuthorName
                author={author}
                className={`${fraunces.className} tw-text-xl tw-font-semibold tw-text-gray-900 dark:tw-text-gray-50 hover:tw-text-primary-700 dark:hover:tw-text-primary-300`}
              />
            </Link>
            {about && (
              <p className="tw-mb-0 tw-mt-1.5 tw-text-[15px] tw-leading-relaxed tw-text-gray-600 dark:tw-text-gray-300">
                {about}
              </p>
            )}
            <div className="tw-mt-4 tw-flex tw-flex-wrap tw-items-center tw-gap-3">
              <FollowButton author={username} btnstyle="pill" />
              <Link
                href={`/@${username}`}
                prefetch={false}
                className="tw-rounded-full tw-border tw-border-solid tw-border-[#C9C0AA] dark:tw-border-[#3A4D44] tw-px-4 tw-py-1 tw-text-[13px] tw-font-semibold tw-text-[#1D2D28] dark:tw-text-[#EBE6D9] tw-no-underline tw-transition-colors hover:tw-border-primary-600 hover:tw-text-primary-700 dark:hover:tw-text-primary-300"
              >
                View blog
              </Link>
              <SocialBar data={data} />
            </div>
          </div>
        </div>

        {recentPosts.length > 0 && (
          <div className="tw-mt-6 tw-border-0 tw-border-t tw-border-solid tw-border-[#E7E0CF] dark:tw-border-[#26382F] tw-pt-5">
            <p className="tw-mb-3 tw-mt-0 tw-font-mono tw-text-[10px] tw-font-semibold tw-uppercase tw-tracking-[0.25em] tw-text-gray-400">
              More from <AuthorName author={author} />
            </p>
            <div className="tw-grid tw-grid-cols-3 tw-gap-3 sm:tw-gap-4">
              {recentPosts.map(post => (
                <Link
                  key={post.permlink}
                  href={`/@${username}/${post.permlink}`}
                  prefetch={false}
                  className="tw-group tw-block tw-no-underline"
                >
                  <div className="tw-relative tw-isolate tw-aspect-[16/11] tw-overflow-hidden tw-rounded-lg">
                    <FutureImage
                      src={post.thumb.url}
                      alt=""
                      fill
                      sizes="96px"
                      maxSrcSetWidth={200}
                      placeholder={post.thumb.blur ? 'blur' : undefined}
                      blurDataURL={post.thumb.blur}
                      className="tw-object-cover tw-transition-transform tw-duration-500 group-hover:tw-scale-[1.05]"
                    />
                  </div>
                  <p className="tw-mb-0 tw-mt-2 tw-overflow-hidden tw-text-[13px] tw-font-semibold tw-leading-snug tw-text-gray-800 dark:tw-text-gray-200 group-hover:tw-text-primary-700 dark:group-hover:tw-text-primary-300 [-webkit-box-orient:vertical] [-webkit-line-clamp:2] [display:-webkit-box]">
                    {post.title}
                  </p>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
